import React from "react";
import { Modal } from "components";
import { Space } from "antd";
import { EMPTY } from "constants/common";
import { WarningFilled } from "@ant-design/icons";
import { map } from "lodash";
import { AICC_LANGUAGE_STATUS, AiccLanguageStatus } from "constants/aiccLanguage";
import { AICC_PROMPT_GROUP_LABEL, AICC_PROMPT_RESET_TARGET, AiccPromptResetTarget } from "constants/aiccPrompt";
import { AiccPromptSharedModel } from "types/model/aiccPrompt";
import { renderRevertOnEditWarning } from "../../LanguageManagement/configs";
import { modelRetirementQuestion, modelRetirementWarning, successorAdvice } from "./ModelSettings/availability";
import { groupSharedPrompts } from "./ModelSettings/sharedPrompts";
import {
  CHANGE_SETTING_COPY,
  CHANGE_SETTING_CURRENT,
  ChangeSettingKey,
  CHANGE_SETTING_SWITCH_TO,
  DISCARD_MESSAGE,
  DISCARD_OK_TEXT,
  DISCARD_TITLE,
  LEAVE_CANCEL_TEXT,
  LEAVE_MESSAGE,
  LEAVE_OK_TEXT,
  LEAVE_TITLE,
  MODEL_DEPRECATED_FALLBACK_NOTE,
  MODEL_DEPRECATED_OK_TEXT,
  MODEL_DEPRECATED_TITLE,
  RESET_MESSAGE,
  RESET_OK_TEXT,
  RESET_PUBLIC_MESSAGE,
  RESET_PUBLIC_OK_TEXT,
  RESET_PUBLIC_TITLE,
  RESET_REVERT_OK_TEXT,
  RESET_TITLE,
} from "./configs";
import styles from "./PromptDetail.module.scss";

export const confirmResetPrompt = (target: AiccPromptResetTarget, status?: AiccLanguageStatus): Promise<boolean> =>
  new Promise((resolve) => {
    const isPublic = target === AICC_PROMPT_RESET_TARGET.public;
    const isPublishReady = status === AICC_LANGUAGE_STATUS.publishReady;
    const message = isPublic ? RESET_PUBLIC_MESSAGE : RESET_MESSAGE;

    Modal.confirm({
      title: isPublic ? RESET_PUBLIC_TITLE : RESET_TITLE,
      content: (
        <div className={styles.confirmContent}>
          {isPublishReady ? (
            <Space direction="vertical" size={12}>
              {renderRevertOnEditWarning()}
              {message}
            </Space>
          ) : (
            message
          )}
        </div>
      ),
      okText: isPublishReady ? RESET_REVERT_OK_TEXT : isPublic ? RESET_PUBLIC_OK_TEXT : RESET_OK_TEXT,
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

export const confirmDiscardDraftChanges = (): Promise<boolean> =>
  new Promise((resolve) => {
    Modal.confirm({
      title: DISCARD_TITLE,
      content: <div className={styles.confirmContent}>{DISCARD_MESSAGE}</div>,
      okText: DISCARD_OK_TEXT,
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

export const confirmLeaveWithUnsavedChanges = (): Promise<boolean> =>
  new Promise((resolve) => {
    Modal.confirm({
      title: LEAVE_TITLE,
      content: <div className={styles.confirmContent}>{LEAVE_MESSAGE}</div>,
      okText: LEAVE_OK_TEXT,
      okButtonProps: { danger: true },
      cancelText: LEAVE_CANCEL_TEXT,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

/**
 * Asked before pointing a prompt at a model the vendor is retiring.
 *
 * A CONFIRMATION, NOT A REFUSAL. The choice is legitimate — the model answers until its shutdown date
 * — so the dialog states when that is and lets the operator proceed. It used to open with "<model> is
 * deprecated", which named the vendor's label rather than the consequence and read as a prohibition.
 *
 * THE SUCCESSOR OUTRANKS THE VENDOR'S PROSE, and replaces it where both exist. `deprecation_note` is
 * extractor output: one real note reads "the document lists gpt-4.1-mini-2025-04-14 as deprecated and
 * says fine-tuned versions follow the same shutdown schedule as their underlying base models" — an id
 * we do not configure, a feature we do not use, and an explanation of how the catalogue reached its
 * verdict rather than of what to do. The successor list answers the operator's actual question, so it
 * takes the line; the prose is the fallback for a row that names no successor.
 */
export const confirmDeprecatedModel = (
  name: string,
  note?: string,
  shutdownAt?: string,
  alternatives?: string[]
): Promise<boolean> =>
  new Promise((resolve) => {
    const successor = successorAdvice(alternatives);
    Modal.confirm({
      title: MODEL_DEPRECATED_TITLE,
      width: 520,
      content: (
        <Space direction="vertical" size={12} className={styles.confirmContent}>
          <span className={styles.confirmWarning}>
            <WarningFilled className={styles.confirmWarningIcon} />
            <span className={styles.confirmModel}>{modelRetirementWarning(name, shutdownAt)}</span>
          </span>
          <span>{successor ?? note ?? MODEL_DEPRECATED_FALLBACK_NOTE}</span>
          <span>{modelRetirementQuestion(name)}</span>
        </Space>
      ),
      okText: MODEL_DEPRECATED_OK_TEXT,
      cancelText: "Cancel",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

interface ChangeSharedSettingOptions {
  setting: ChangeSettingKey;
  currentValue: string;
  nextValue: string;
  sharesModelWith: AiccPromptSharedModel[];
}

export const confirmSharedSettingChange = ({
  setting,
  currentValue,
  nextValue,
  sharesModelWith,
}: ChangeSharedSettingOptions): Promise<boolean> =>
  new Promise((resolve) => {
    const copy = CHANGE_SETTING_COPY[setting];

    Modal.confirm({
      title: copy.title,
      width: 560,
      content: (
        <Space direction="vertical" size={12} className={styles.confirmContent}>
          <span>
            {`${CHANGE_SETTING_CURRENT} `}
            <span className={styles.confirmModel}>{currentValue || EMPTY}</span>
            {` ➔ ${CHANGE_SETTING_SWITCH_TO} `}
            <span className={styles.confirmModel}>{nextValue || EMPTY}</span>
          </span>

          <Space direction="vertical" size={4}>
            <span className={styles.confirmWarning}>
              <WarningFilled className={styles.confirmWarningIcon} />
              <span>{copy.affects(sharesModelWith.length)}</span>
            </span>
            {map(groupSharedPrompts(sharesModelWith), ({ group, prompts }) => (
              <span key={group} className={styles.confirmSharedGroup}>
                <span className={styles.confirmModel}>{`${AICC_PROMPT_GROUP_LABEL[group]}: `}</span>
                {map(prompts, "name").join(", ")}
              </span>
            ))}
          </Space>

          <span>{copy.question}</span>
        </Space>
      ),
      okText: copy.okText,
      cancelText: "Cancel",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
